import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import * as searchApi from '@/features/search/api';
import {
  FIELD_META,
  SEARCH_FIELDS,
  Suggestion,
  buildSearchRequest,
  getSuggestions,
  isEmptyQuery,
  parseQuery,
} from '@/features/search/query';
import { SuggestionLead } from '@/features/search/sparkql-input';
import { useSearchVocab } from '@/features/search/use-vocab';
import { SavedSearch, useSavedSearches } from '@/features/search/saved';
import { Item } from '@/features/backlog/types';
import { taskTypeMeta } from '@/features/backlog/constants';
import { searchPath, searchViewPath, taskPath } from '@/constants/routes';
import { Search, Tag, TaskTypeGlyph, Trash, X } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

/** Enter-key hint glyph. */
const ReturnKey = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <polyline points="9 10 4 15 9 20" />
    <path d="M20 4v7a4 4 0 0 1-4 4H4" />
  </svg>
);

/** Compact cross-board result row shown in the dropdown. */
function ResultRow({ item, boardName, active, onClick }: { item: Item; boardName?: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors', active ? 'bg-layer' : 'hover:bg-layer')}
    >
      <TaskTypeGlyph type={item.type} color={taskTypeMeta(item.type).color} />
      <span className="item-key shrink-0 rounded px-1 py-px tnum text-[10px] font-semibold">{item.key}</span>
      <span className="min-w-0 flex-1 truncate text-[13px] text-fg-soft">{item.title || 'Untitled'}</span>
      {boardName ? <span className="shrink-0 truncate rounded-full bg-layer-strong px-2 py-0.5 text-[10.5px] font-medium text-fg-faint">{boardName}</span> : null}
    </button>
  );
}

/**
 * The centered global search in the header. A single input drives a token-aware "SparkQL" query
 * (see features/search/query) with:
 *  - autocomplete that suggests field keys, then their concrete values (boards / members / statuses /
 *    labels / types / priorities);
 *  - live cross-board results that open the item directly;
 *  - personal saved searches + a one-click Save;
 *  - Enter / "View all" → the full /search results page.
 * Cmd/Ctrl-K (or "/") focuses it from anywhere.
 */
export default function GlobalSearch() {
  const { boards, members } = useAppData();
  const { me } = useIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const [urlParams, setUrlParams] = useSearchParams();
  const { searches: saved, save, remove } = useSavedSearches();
  const onSearchPage = location.pathname === '/search';

  // On the /search results page this header box IS the page's query — mirror the active ?q into it
  // (so the results page has no separate input of its own on desktop).
  const urlQuery = location.pathname === '/search' ? urlParams.get('query') ?? '' : null;

  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Item[]>([]);
  const [resultTotal, setResultTotal] = useState(0); // full match count (results is only the top few)
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [naming, setNaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const { labels, statusLabels } = useSearchVocab();

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const reqSeq = useRef(0);

  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const ctx = useMemo(() => ({ boards, members, meId: me?.id, labels, statusLabels }), [boards, members, me, labels, statusLabels]);

  // Keep the box in sync with the results page's ?q (landing on /search, changing the query there).
  useEffect(() => {
    if (urlQuery !== null) setValue(urlQuery);
  }, [urlQuery]);

  // Focus shortcut: Cmd/Ctrl-K, or "/" when not already typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === '/' && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const runnable = !isEmptyQuery(value);
  const parsed = useMemo(() => parseQuery(value), [value]);

  // Debounced live results.
  useEffect(() => {
    if (!open || !runnable) {
      setResults([]);
      setResultTotal(0);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const req = buildSearchRequest(parsed, ctx, 6);
    const t = window.setTimeout(() => {
      const seq = ++reqSeq.current; // ignore a slow earlier response landing after a newer one
      searchApi
        .searchItems(req) // no explicit sort → backend ranks by relevance when there's free text
        .then((r) => { if (seq === reqSeq.current) { setResults(r.data); setResultTotal(r.total); } })
        .catch(() => { if (seq === reqSeq.current) { setResults([]); setResultTotal(0); } })
        .finally(() => { if (seq === reqSeq.current) setLoading(false); });
    }, 170);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, runnable, value, ctx]);

  const { replaceStart, suggestions } = useMemo(
    () => (open ? getSuggestions(value, ctx, 6) : { replaceStart: 0, suggestions: [] as Suggestion[] }),
    [open, value, ctx],
  );
  const showSuggestions = value.trim() !== '' && !/\s$/.test(value) && suggestions.length > 0;

  // Reset the active row whenever the composition changes.
  useEffect(() => setActive(-1), [value, open]);

  const applySuggestion = useCallback(
    (s: Suggestion) => {
      const next = value.slice(0, replaceStart) + s.insert;
      setValue(next);
      inputRef.current?.focus();
    },
    [value, replaceStart],
  );

  const openItem = useCallback(
    (item: Item) => {
      const slug = boardById.get(item.board_id)?.slug;
      if (slug) navigate(taskPath(slug, item.key));
      setOpen(false);
    },
    [boardById, navigate],
  );

  const runQuery = useCallback(
    (query: string) => {
      // On the results page, change the query (back to page 1) and keep the table view (cols/sort) intact.
      if (onSearchPage) {
        setUrlParams((prev) => {
          const p = new URLSearchParams(prev);
          if (query.trim()) p.set('query', query);
          else p.delete('query');
          p.delete('page');
          return p;
        });
      } else {
        navigate(searchPath(query));
      }
      setOpen(false);
    },
    [navigate, onSearchPage, setUrlParams],
  );

  // Applying a saved search restores its full view — query + columns + sort — exactly.
  const applySaved = useCallback(
    (s: SavedSearch) => {
      navigate(searchViewPath(s.q, s.cols, s.sort));
      setOpen(false);
    },
    [navigate],
  );

  // Flat list of arrow-navigable rows in render order (Enter targets the active one).
  const navRows = useMemo(() => {
    const rows: { key: string; run: () => void }[] = [];
    if (value.trim() === '') {
      saved.forEach((s) => rows.push({ key: `saved-${s.id}`, run: () => applySaved(s) }));
      return rows;
    }
    if (showSuggestions) suggestions.forEach((s) => rows.push({ key: s.id, run: () => applySuggestion(s) }));
    results.forEach((it) => rows.push({ key: `res-${it.id}`, run: () => openItem(it) }));
    if (runnable) rows.push({ key: 'view-all', run: () => runQuery(value) });
    return rows;
  }, [value, showSuggestions, suggestions, results, runnable, saved, applySuggestion, openItem, runQuery, applySaved]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (naming) { setNaming(false); return; }
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (navRows.length ? (a + 1) % navRows.length : -1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (navRows.length ? (a - 1 + navRows.length) % navRows.length : -1));
      return;
    }
    if (e.key === 'Tab' && showSuggestions && suggestions.length) {
      // Tab completes the first (or active) suggestion.
      e.preventDefault();
      const idx = active >= 0 && active < suggestions.length ? active : 0;
      applySuggestion(suggestions[idx]);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && active < navRows.length) navRows[active].run();
      else if (runnable) runQuery(value);
    }
  };

  const doSave = () => {
    const name = nameDraft.trim();
    if (!name && !value.trim()) return;
    // On the results page, capture the current table view (cols/sort) so the saved search restores it.
    const view = onSearchPage ? { cols: urlParams.get('cols') ?? undefined, sort: urlParams.get('sort') ?? undefined } : undefined;
    save(name || value.trim(), value, view);
    setNaming(false);
    setNameDraft('');
  };

  const empty = value.trim() === '';
  let navIndex = -1; // running index aligned with navRows for the active highlight

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={cn(
          // Raised surface + subtle shadow so the search stands out from the header bar, exactly like
          // the active view-switcher button. `surface-raised` is white in light theme and the proper
          // raised colour in dark theme (never literal white).
          'flex h-9 items-center gap-2 rounded-full border px-3 shadow-sm transition-colors',
          open ? 'border-accent/50 bg-surface-raised' : 'border-line bg-surface-raised hover:border-line-strong',
        )}
      >
        <Search className="h-4 w-4 shrink-0 text-fg-faint" />
        <input
          ref={inputRef}
          value={value}
          // Open the flyout on ANY interaction — not just first focus. The input can stay focused with
          // the flyout closed (e.g. after running a query on /search, or an outside click that didn't
          // move focus); without re-opening here, a click or keystroke into the still-focused box would
          // leave the flyout — and the chip autocomplete — un-mounted.
          onChange={(e) => { setValue(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search all boards…  (try type:bug priority:high)"
          aria-label="Search all boards"
          className="min-w-0 flex-1 bg-transparent text-[13px] text-fg placeholder:text-fg-ghost focus:outline-none"
        />
        {value ? (
          <button type="button" onClick={() => { setValue(''); inputRef.current?.focus(); }} aria-label="Clear" className="shrink-0 text-fg-faint transition-colors hover:text-fg-soft">
            <X className="h-4 w-4" />
          </button>
        ) : (
          <kbd className="hidden shrink-0 rounded border border-line px-1.5 text-[10px] font-medium text-fg-ghost sm:block">⌘K</kbd>
        )}
      </div>

      {open ? (
        <div className="thin-scroll absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[70vh] overflow-auto rounded-xl border border-line bg-surface-raised p-1.5 shadow-2xl shadow-black/60">
          {empty ? (
            <div>
              {saved.length ? (
                <div className="mb-1">
                  <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-fg-ghost">Saved searches</div>
                  {saved.map((s) => {
                    navIndex += 1;
                    const idx = navIndex;
                    return (
                      <div key={s.id} className={cn('group flex items-center gap-1 rounded-lg', active === idx && 'bg-layer')}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applySaved(s)}
                          className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-layer"
                        >
                          <Search className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
                          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg-soft">{s.name}</span>
                          <span className="shrink-0 truncate font-mono text-[11px] text-fg-ghost">{s.q}</span>
                        </button>
                        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => remove(s.id)} aria-label={`Delete ${s.name}`} className="mr-1 shrink-0 rounded p-1 text-fg-ghost opacity-0 transition hover:text-danger group-hover:opacity-100">
                          <Trash className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-fg-ghost">Search across every board</div>
              <div className="flex flex-wrap gap-1.5 px-2 pb-2 pt-1">
                {SEARCH_FIELDS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setValue((v) => (v ? v.replace(/\s*$/, ' ') : '') + `${f}:`); inputRef.current?.focus(); }}
                    title={FIELD_META[f].hint}
                    className="rounded-full border border-line bg-layer px-2.5 py-1 font-mono text-[11.5px] text-fg-soft transition-colors hover:border-accent/40 hover:text-fg"
                  >
                    {f}:
                  </button>
                ))}
              </div>
              <div className="px-2 pb-1 text-[11px] leading-relaxed text-fg-faint">
                e.g. <span className="font-mono text-fg-soft">board:Alpha type:bug priority:high -status:Done login</span>
              </div>
            </div>
          ) : (
            <div>
              {showSuggestions ? (
                <div className="mb-1">
                  {suggestions.map((s) => {
                    navIndex += 1;
                    const idx = navIndex;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applySuggestion(s)}
                        className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors', active === idx ? 'bg-layer' : 'hover:bg-layer')}
                      >
                        <SuggestionLead s={s} memberById={memberById} />
                        <span className="min-w-0 flex-1 truncate text-[13px] text-fg-soft">
                          {s.category === 'field' ? <span className="font-mono">{s.label}</span> : s.label}
                        </span>
                        {s.detail ? <span className="shrink-0 truncate text-[11px] text-fg-ghost">{s.detail}</span> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div>
                <div className="flex items-center justify-between px-2 pb-1 pt-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-ghost">Results</span>
                  {loading ? (
                    <span className="text-[11px] text-fg-ghost">Searching…</span>
                  ) : resultTotal > 0 ? (
                    <span className="tnum text-[11px] text-fg-ghost">{resultTotal.toLocaleString()} match{resultTotal === 1 ? '' : 'es'}</span>
                  ) : null}
                </div>
                {results.length === 0 && !loading ? (
                  <div className="px-2 py-3 text-[12.5px] text-fg-faint">No matching items.</div>
                ) : (
                  results.map((it) => {
                    navIndex += 1;
                    const idx = navIndex;
                    return (
                      <ResultRow key={it.id} item={it} boardName={boardById.get(it.board_id)?.name} active={active === idx} onClick={() => openItem(it)} />
                    );
                  })
                )}
                {runnable
                  ? (() => {
                      navIndex += 1;
                      const idx = navIndex;
                      return (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => runQuery(value)}
                          className={cn('mt-0.5 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] font-medium text-accent transition-colors', active === idx ? 'bg-layer' : 'hover:bg-layer')}
                        >
                          <Search className="h-3.5 w-3.5" />
                          <span className="flex-1">{!loading && resultTotal > 0 ? `View all ${resultTotal.toLocaleString()} result${resultTotal === 1 ? '' : 's'}` : 'View all results'}</span>
                          <ReturnKey className="text-fg-ghost" />
                        </button>
                      );
                    })()
                  : null}
              </div>
            </div>
          )}

          {/* Footer: save current search. */}
          {!empty ? (
            <div className="mt-1 border-t border-line pt-1">
              {naming ? (
                <div className="flex items-center gap-2 px-1.5 py-1">
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doSave(); } if (e.key === 'Escape') { e.stopPropagation(); setNaming(false); } }}
                    placeholder="Name this search…"
                    className="min-w-0 flex-1 rounded-lg border border-line bg-surface-inset px-2 py-1 text-[12.5px] text-fg focus:border-accent/50 focus:outline-none"
                  />
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={doSave} className="shrink-0 rounded-lg bg-accent px-2.5 py-1 text-[12px] font-semibold text-ink">Save</button>
                </div>
              ) : (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setNaming(true); setNameDraft(''); }}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] text-fg-faint transition-colors hover:bg-layer hover:text-fg-soft"
                >
                  <Tag className="h-3.5 w-3.5" />
                  Save this search
                </button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
